// /amazon-overview - the Amazon buildings' path to COD, over the pages its own nav lists:
// where every building stands, one building at a time, what is needed from Amazon, the
// EUSDR questions for Amazon (AmazonEusdrQuestionsRoute), and the path every building
// follows. Reached by URL only, and shareable outside InRange.
// docs/Y2026-Q4/design-amazon-overview.md, design-amazon-overview-sharing.md,
// design-amazon-item-owners.md
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";

import { UserContext } from "../auth";
import { useReportView } from "../api/hooks/useShares";
import { itemId } from "../api/hooks/useAmazonOwners";
import { BuildingView } from "./amazonOverview/BuildingView";
import { today } from "./amazonOverview/dates";
import { Heatmap } from "./amazonOverview/Heatmap";
import type { Edit, Target } from "./amazonOverview/editContext";
import { EditProvider } from "./amazonOverview/editing";
import { OverviewHeading, WhenLoaded } from "./amazonOverview/Frame";
import { OwnersProvider } from "./amazonOverview/ItemOwners";
import { neededItems } from "./amazonOverview/openIssues";
import { NO_OVERRIDES } from "./amazonOverview/overrides";
import {
  carriedSearch,
  pageLabel,
  pagePath,
  sitePath,
} from "./amazonOverview/overviewPages";
import { siteCode } from "./amazonOverview/siteCode";
import { sitesListed, useEverySite } from "./amazonOverview/siteScope";
import { StoryMap } from "./amazonOverview/StoryMap";
import { useShownBuildings } from "./amazonOverview/useShownBuildings";
import styles from "./amazonOverview/amazonOverview.module.css";

type Loaded = ReturnType<typeof useShownBuildings>;

/** Every page of the overview: the buildings loaded once, and the edit switch (?edit=1, staff
 * only) that writes over Portfolio status and Site details. */
export const AmazonOverviewLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const loaded = useShownBuildings();
  const { byBuilding, platform, steps } = loaded;
  const { role } = useContext(UserContext);
  const editing = new URLSearchParams(location.search).get("edit") === "1" && role === "admin";
  const [selected, setSelected] = useState<Target | null>(null);
  useEffect(() => setSelected(null), [location.pathname]);

  // Process overview keeps its view in the history entry (StoryMap), so the step opens in
  // its pane.
  const openStepOne = useCallback(
    (stepId: string) => {
      const step = steps.find((s) => s.id === stepId);
      navigate(
        { pathname: pagePath("process"), search: carriedSearch(location.search) },
        {
          state: {
            story: {
              chapter: step?.chapter ?? 0,
              present: false,
              pane: { kind: "step", id: step?.inBox ?? stepId },
            },
          },
        },
      );
    },
    [navigate, location.search, steps],
  );
  // The items still owed on the sites Needed from Amazon lists, which each person's open
  // count counts.
  const everySite = useEverySite();
  const owedItems = useMemo(
    () =>
      new Set(
        neededItems(sitesListed(loaded.shown, everySite), steps, "name", today()).flatMap((s) =>
          s.items
            .filter((i) => i.state !== "received")
            .map((i) => itemId(s.building.project_id, i.itemKey)),
        ),
      ),
    [loaded.shown, steps, everySite],
  );
  const edit: Edit = useMemo(
    () => ({
      editing,
      selected,
      select: setSelected,
      overrides: (id) => byBuilding.get(id) ?? NO_OVERRIDES,
      platform: (id) => platform.get(id),
      openStepOne,
    }),
    [editing, selected, byBuilding, platform, openStepOne],
  );
  return (
    <EditProvider value={edit}>
      <div className={styles.page}>
        <OwnersProvider owedItems={owedItems}>
          <Outlet context={loaded} />
        </OwnersProvider>
      </div>
    </EditProvider>
  );
};

const useLoaded = () => useOutletContext<Loaded>();

const data = (loaded: Loaded, render: () => JSX.Element) => (
  <WhenLoaded data={loaded}>{render}</WhenLoaded>
);

export const ProcessOverviewPage = () => {
  const { blueprint, steps } = useLoaded();
  useReportView("amazon-overview", pageLabel("process"));
  return (
    <>
      <OverviewHeading title={pageLabel("process")} />
      <p className={styles.sub}>
        Every Amazon building follows this path, set by the SPPA, the MSPPA and Amazon's
        EUSDR. Read it chapter by chapter; steps side by side happen at the same time.
      </p>
      {blueprint.isLoading ? (
        <p className={styles.muted}>Loading the shared blueprint…</p>
      ) : (
        <StoryMap steps={steps} blueprintError={Boolean(blueprint.error)} />
      )}
    </>
  );
};

export const PortfolioStatusPage = () => {
  const loaded = useLoaded();
  const navigate = useNavigate();
  const { search } = useLocation();
  useReportView("amazon-overview", pageLabel("portfolio"));
  const open = useCallback(
    (projectId: string) => {
      const b = loaded.shown.find((x) => x.project_id === projectId);
      if (b) navigate({ pathname: sitePath(siteCode(b)), search: carriedSearch(search) });
    },
    [loaded.shown, navigate, search],
  );
  return (
    <>
      <OverviewHeading title={pageLabel("portfolio")} />
      {data(loaded, () => (
        <Heatmap buildings={loaded.shown} steps={loaded.steps} onOpen={open} />
      ))}
    </>
  );
};

export const SiteDetailsPage = () => {
  const loaded = useLoaded();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { code } = useParams();
  const building = code
    ? loaded.shown.find((b) => siteCode(b) === code)
    : undefined;
  useReportView(
    "amazon-overview",
    building ? `${pageLabel("sites")} (${siteCode(building)})` : null,
  );
  const select = useCallback(
    (projectId: string) => {
      const b = loaded.shown.find((x) => x.project_id === projectId);
      if (b) navigate({ pathname: sitePath(siteCode(b)), search: carriedSearch(search) });
    },
    [loaded.shown, navigate, search],
  );
  // With no building named, or one no longer shown, the first opens; named in the URL, it
  // stays open when an override moves it down the order.
  const first = loaded.shown[0];
  if (!loaded.notes.isLoading && first && !building)
    return (
      <Navigate
        to={{ pathname: sitePath(siteCode(first)), search: carriedSearch(search) }}
        replace
      />
    );
  return (
    <>
      <OverviewHeading title={pageLabel("sites")} />
      {data(loaded, () => (
        <BuildingView
          buildings={loaded.shown}
          steps={loaded.steps}
          projectId={building?.project_id ?? null}
          onSelect={select}
        />
      ))}
    </>
  );
};

/** The overview opens on Portfolio status, keeping the edit switch (Noaa, 22 Oct 2026). */
export const ToFirstPage = () => {
  const { search } = useLocation();
  return (
    <Navigate to={{ pathname: pagePath("portfolio"), search: carriedSearch(search) }} replace />
  );
};
